import React, { useEffect, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';

import {
  AppBar,
  Box,
  BriksioLogo,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  NavigationMenu,
  Toolbar,
  Typography,
} from 'components';
import { useIsMobile } from 'hooks';

import { useMenu } from './Menu/useMenu';
import { NavigationItem } from './NavigationItem';
import { UserMenu } from './UserMenu';

import { useNavigate, useSelector, useTour, useTranslation } from 'third-party';

import {
  disclaimerUrl,
  helpCenterUrl,
  privacyPolicyUrl,
  termsAndConditionsUrl,
  userPaymentListUrl,
} from 'constants/url';

import { useApp } from 'modules/app';

import { appSelectors } from 'selectors';

export const Navigation = () => {
  const menu = useMenu();
  const navigate = useNavigate();
  const { isOpen } = useTour();
  const isNavigationCollapsed = useSelector(appSelectors.isNavigationCollapsed);
  const selectedOrg = useSelector(appSelectors.selectedTenant);
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { toggleNavigation } = useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (isMobile && isOpen) {
      setMobileOpen(true);
    }
  }, [isMobile, isOpen]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onLogoClick = () => {
    navigate(userPaymentListUrl());
  };

  const [anchorLegalEl, setAnchorLegalEl] = React.useState<null | HTMLElement>(
    null,
  );
  const openLegal = Boolean(anchorLegalEl);

  const handleLegalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorLegalEl(event.currentTarget);
  };

  const handleLegalClose = () => {
    setAnchorLegalEl(null);
  };

  const handleMenuClick = (url: string) => {
    window.open(url, '_blank');
    handleLegalClose();
  };

  const brandName = selectedOrg?.tenant?.name ?? 'Briksio';

  const navigationContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        onClick={onLogoClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '20px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        <BriksioLogo size={40} />
        {!isNavigationCollapsed && (
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'primary.main',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {brandName}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: '16px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'divider',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'grey.300',
          },
        }}
      >
        {menu.map((it, index) => (
          <NavigationMenu
            key={index}
            menu={it}
            isCollapsed={isMobile ? false : isNavigationCollapsed}
          />
        ))}

        {/* Help & Legal */}
        <NavigationItem
          icon={
            <SecurityOutlinedIcon
              sx={{ fontSize: '20px', color: 'text.secondary' }}
            />
          }
          title={t('navigation.support.helpAndLegal')}
          isCollapsed={isNavigationCollapsed}
          handleClick={handleLegalClick}
          isMobile={isMobile}
        />
        <Menu
          id="legal-menu"
          anchorEl={anchorLegalEl}
          open={openLegal}
          onClose={handleLegalClose}
          transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <MenuItem onClick={() => window.open(helpCenterUrl(), '_blank')}>
            {t('navigation.support.helpAndSupport')}
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick(disclaimerUrl())}>
            {t('navigation.support.disclaimer')}
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick(privacyPolicyUrl())}>
            {t('navigation.support.privacyPolicy')}
          </MenuItem>
          <MenuItem onClick={() => handleMenuClick(termsAndConditionsUrl())}>
            {t('navigation.support.termsAndConditions')}
          </MenuItem>
        </Menu>
      </Box>

      <UserMenu />

      {!isMobile && (
        <Box
          onClick={toggleNavigation}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '6px',
            height: '48px',
            bgcolor: 'divider',
            cursor: 'col-resize',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            '&:hover': {
              '& .collapse-icon': { color: 'primary.main' },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: '-12px',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              color: 'text.secondary',
              transition: 'color 0.2s',
            }}
          >
            {isNavigationCollapsed ? (
              <ChevronRightIcon
                className="collapse-icon"
                sx={{ fontSize: '14px' }}
              />
            ) : (
              <ChevronLeftIcon
                className="collapse-icon"
                sx={{ fontSize: '14px' }}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme => theme.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
          }}
        >
          <Toolbar sx={{ gap: '12px' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: 'text.secondary' }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
              }}
              onClick={onLogoClick}
            >
              <BriksioLogo />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: 'text.primary',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                Briksio
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 260,
              bgcolor: 'background.paper',
            },
          }}
        >
          {navigationContent}
        </Drawer>
      </>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>{navigationContent}</Box>
  );
};
